import moment from 'moment';

import { DiningOption } from '../types';

export type DiningOptionWithIndex = {
  option: DiningOption;
  optionIndex: number;
  dayNumber: number;
};

export type DiningDateGroup = {
  date: string;
  options: DiningOptionWithIndex[];
};

export const getDiningDayNumbers = (option: DiningOption, lastDayNumber = Infinity): number[] => {
  const dayNumbers = (option.dayNumberRange ?? []).filter((day) => day <= lastDayNumber);
  return dayNumbers.length > 1 ? dayNumbers : [option.dayNumber || 0];
};

export const getLastDayNumber = (fromDate: moment.MomentInput, toDate?: moment.MomentInput) =>
  toDate ? moment(toDate).startOf('day').diff(moment(fromDate).startOf('day'), 'days') : Infinity;

export const resolveDiningDayNumber = (
  option: DiningOption,
  selectedDayNumber?: number,
  lastDayNumber = Infinity
) => {
  const dayNumbers = getDiningDayNumbers(option, lastDayNumber);
  if (selectedDayNumber !== undefined && dayNumbers.includes(selectedDayNumber)) {
    return selectedDayNumber;
  }
  return dayNumbers.includes(option.dayNumber || 0) ? option.dayNumber || 0 : dayNumbers[0];
};

export const getDiningDate = (fromDate: moment.MomentInput, dayNumber: number) =>
  moment(fromDate).add(dayNumber, 'days').format('YYYY-MM-DD');

export function groupDiningOptionsByDate(
  diningOptions: DiningOption[],
  fromDate: moment.MomentInput,
  selectedDayNumbers: Array<number | undefined> = [],
  lastDayNumber = Infinity
): DiningDateGroup[] {
  const groups = new Map<string, DiningOptionWithIndex[]>();

  diningOptions.forEach((option, optionIndex) => {
    const dayNumber = resolveDiningDayNumber(
      option,
      selectedDayNumbers[optionIndex],
      lastDayNumber
    );
    const date = getDiningDate(fromDate, dayNumber);

    const group = groups.get(date);

    if (group) {
      group.push({ option, optionIndex, dayNumber });
    } else {
      groups.set(date, [{ option, optionIndex, dayNumber }]);
    }
  });

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, options]) => ({ date, options }));
}
